import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY_PREFIX = "@sacgpt:upgrade_reminder_";
const REMIND_DELAY_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Hook to manage upgrade modal "remind later" state via localStorage
 * @param {string} userId - User ID for localStorage key
 */
export const useUpgradeModal = (userId) => {
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [lastPlatform, setLastPlatform] = useState(null);

  const storageKey = `${STORAGE_KEY_PREFIX}${userId}`;

  // Check if modal should be shown (24h passed or never clicked "remind later")
  const checkShouldShow = useCallback(() => {
    if (!userId) return false;

    const stored = localStorage.getItem(storageKey);

    if (!stored) return true; // Never clicked "remind later"

    try {
      const { remindAfter, platform } = JSON.parse(stored);
      setLastPlatform(platform);
      return Date.now() >= remindAfter; // 24h passed?
    } catch {
      return true; // Invalid data, show modal
    }
  }, [userId, storageKey]);

  // Initialize state on mount and when userId changes
  useEffect(() => {
    setShouldShowModal(checkShouldShow());
  }, [checkShouldShow]);

  // Mark "remind later" - saves timestamp +24h
  const markRemindLater = useCallback(
    (platform) => {
      if (!userId) return;

      const remindAfter = Date.now() + REMIND_DELAY_MS;
      localStorage.setItem(
        storageKey,
        JSON.stringify({ remindAfter, platform })
      );
      setLastPlatform(platform);
      setShouldShowModal(false);
    },
    [userId, storageKey]
  );

  // Mark dismissed (user upgraded) - clears state
  const markDismissed = useCallback(() => {
    if (!userId) return;

    localStorage.removeItem(storageKey);
    setLastPlatform(null);
    setShouldShowModal(false);
  }, [userId, storageKey]);

  // Force refresh the check (useful after time passes)
  const refreshCheck = useCallback(() => {
    setShouldShowModal(checkShouldShow());
  }, [checkShouldShow]);

  return {
    shouldShowModal,
    markRemindLater,
    markDismissed,
    refreshCheck,
    lastPlatform,
  };
};

export default useUpgradeModal;
